import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiSearch, FiShare2, FiEdit, FiVideo, FiPlay, FiMonitor, FiMail, FiSend, FiFilm, FiPhone, FiMessageCircle, FiBook, FiCpu, FiArrowLeft } = FiIcons;

const ServicesGrid = () => {
  const [flippedCards, setFlippedCards] = useState(new Set());

  const services = [
    { 
      name: "Facebook & Instagram Ads", 
      icon: FiTarget,
      description: "Create high-converting social media campaigns with advanced targeting to reach your ideal customers and maximize ROI.",
      features: ["Custom Audience Creation", "A/B Testing", "Performance Analytics", "Creative Design"]
    },
    { 
      name: "Google Ads", 
      icon: FiSearch,
      description: "Drive qualified traffic with strategic Google Ads campaigns optimized for conversions and cost-efficiency.",
      features: ["Keyword Research", "Ad Copy Writing", "Landing Page Optimization", "Bid Management"]
    },
    { 
      name: "Social Media Posting", 
      icon: FiShare2,
      description: "Consistent, engaging content across all platforms to build brand awareness and community engagement.",
      features: ["Content Calendar", "Visual Design", "Hashtag Strategy", "Community Management"]
    },
    { 
      name: "Blog & Content Writing", 
      icon: FiEdit,
      description: "SEO-optimized blog posts and content that educates your audience and drives organic traffic.",
      features: ["SEO Writing", "Research & Planning", "Content Strategy", "Editorial Calendar"]
    },
    { 
      name: "AI Video Creation", 
      icon: FiVideo,
      description: "Cutting-edge AI-powered video creation for marketing campaigns, social media, and promotional content.",
      features: ["AI Script Writing", "Automated Editing", "Voice Synthesis", "Brand Integration"]
    },
    { 
      name: "Ads Video Creation", 
      icon: FiPlay,
      description: "Professional video advertisements designed to capture attention and drive conversions across platforms.",
      features: ["Concept Development", "Professional Editing", "Motion Graphics", "Multi-Format Output"]
    },
    { 
      name: "Product Demo Videos", 
      icon: FiMonitor,
      description: "Clear, compelling product demonstrations that showcase features and benefits to potential customers.",
      features: ["Script Writing", "Screen Recording", "Professional Narration", "Call-to-Action Integration"]
    },
    { 
      name: "SEO", 
      icon: FiSearch,
      description: "Comprehensive search engine optimization to improve your website's visibility and organic rankings.",
      features: ["Technical SEO", "On-Page Optimization", "Link Building", "Performance Tracking"]
    },
    { 
      name: "Social Profile Optimization", 
      icon: FiShare2,
      description: "Optimize your social media profiles for maximum visibility, engagement, and professional appearance.",
      features: ["Profile Setup", "Bio Optimization", "Visual Branding", "Content Strategy"]
    },
    { 
      name: "Email Marketing", 
      icon: FiMail,
      description: "Strategic email campaigns that nurture leads, retain customers, and drive sales through personalized messaging.",
      features: ["Campaign Design", "List Segmentation", "Automation Setup", "Performance Analytics"]
    },
    { 
      name: "Newsletter Marketing", 
      icon: FiSend,
      description: "Regular newsletters that keep your audience engaged with valuable content and company updates.",
      features: ["Content Curation", "Design Templates", "Subscriber Management", "Engagement Tracking"]
    },
    { 
      name: "Animated Video Explainers", 
      icon: FiFilm,
      description: "Engaging animated videos that explain complex concepts, products, or services in an easy-to-understand format.",
      features: ["Storyboard Creation", "2D/3D Animation", "Voiceover Production", "Brand Customization"]
    },
    { 
      name: "Calling Services", 
      icon: FiPhone,
      description: "Professional outbound calling services for lead generation, customer support, and sales conversion.",
      features: ["Lead Qualification", "Appointment Setting", "Customer Follow-up", "Sales Conversion"]
    },
    { 
      name: "WhatsApp Marketing", 
      icon: FiMessageCircle,
      description: "Reach your customers directly on the world's most popular messaging app with targeted broadcast campaigns and automated support.",
      features: ["Automated Chatbots", "Broadcast Campaigns", "Customer Support API", "High Open Rates"]
    },
    { 
      name: "Course Designing", 
      icon: FiBook,
      description: "Comprehensive online course creation from concept to completion, including content, design, and delivery.",
      features: ["Curriculum Development", "Video Production", "Interactive Elements", "Learning Management"]
    },
    { 
      name: "AI Bot Creation", 
      icon: FiCpu,
      description: "Custom AI chatbots and automation tools to improve customer service and streamline business processes.",
      features: ["Natural Language Processing", "Integration Setup", "Training & Optimization", "Analytics Dashboard"]
    }
  ];

  const handleCardClick = (index) => {
    const newFlippedCards = new Set(flippedCards);
    if (newFlippedCards.has(index)) {
      newFlippedCards.delete(index);
    } else {
      newFlippedCards.add(index);
    }
    setFlippedCards(newFlippedCards);
  };

  const handleMouseLeave = (index) => {
    const newFlippedCards = new Set(flippedCards);
    newFlippedCards.delete(index);
    setFlippedCards(newFlippedCards);
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section className="py-24 relative" style={{ backgroundColor: 'rgba(30, 15, 33, 0.7)' }}>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-purple-300">
            Our Services
          </h2>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Click on any service to learn more about what we offer
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative h-64 cursor-pointer group perspective-1000"
              onClick={() => handleCardClick(index)}
              onMouseLeave={() => handleMouseLeave(index)}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                className="relative w-full h-full preserve-3d transition-transform duration-700"
                animate={{ 
                  rotateY: flippedCards.has(index) ? 180 : 0 
                }}
                style={{ 
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                <motion.div
                  className="absolute inset-0 w-full h-full p-8 bg-purple-900 bg-opacity-20 backdrop-blur-sm rounded-2xl border border-purple-800 hover:border-yellow-400 transition-all duration-300 text-center backface-hidden"
                  style={{ backfaceVisibility: 'hidden' }}
                  whileHover={{ 
                    boxShadow: '0 0 30px rgba(255, 215, 0, 0.3)',
                  }}
                >
                  <motion.div 
                    className="mb-6 flex justify-center"
                    animate={{ rotate: [0, 5, 0, -5, 0] }}
                    transition={{ 
                      duration: 6, 
                      repeat: Infinity,
                      delay: index * 0.2,
                      ease: "easeInOut" 
                    }}
                  >
                    <motion.div 
                      className="p-4 rounded-full bg-purple-800 bg-opacity-50 group-hover:bg-purple-700 transition-colors duration-300"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <SafeIcon 
                        icon={service.icon} 
                        className="text-3xl group-hover:scale-110 transition-transform duration-300" 
                        style={{ color: '#FFD700' }} 
                      />
                    </motion.div>
                  </motion.div>
                  <h3 className="text-lg font-semibold text-purple-100 group-hover:text-yellow-300 transition-colors duration-300 mb-4">
                    {service.name}
                  </h3>
                  <motion.p 
                    className="text-sm text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    Click to learn more
                  </motion.p>
                </motion.div>

                <motion.div
                  className="absolute inset-0 w-full h-full p-6 bg-gradient-to-br from-purple-900/40 to-purple-800/40 backdrop-blur-sm rounded-2xl border border-yellow-400 text-left overflow-y-auto"
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)'
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <SafeIcon 
                      icon={service.icon} 
                      className="text-2xl" 
                      style={{ color: '#FFD700' }} 
                    />
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="p-1 rounded-full bg-purple-700/50 hover:bg-purple-600/70 transition-colors duration-300"
                    >
                      <SafeIcon 
                        icon={FiArrowLeft} 
                        className="text-sm text-purple-200" 
                      />
                    </motion.div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-yellow-300 mb-3 leading-tight">
                    {service.name}
                  </h3>
                  
                  <p className="text-sm text-purple-100 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div>
                    <h4 className="text-xs font-semibold text-yellow-400 mb-2 uppercase tracking-wide">
                      Key Features:
                    </h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li 
                          key={featureIndex}
                          className="text-xs text-purple-200 flex items-center"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: featureIndex * 0.1 }}
                        >
                          <span className="w-1 h-1 bg-yellow-400 rounded-full mr-2 flex-shrink-0"></span>
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;